(() => {
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initProgress() {
    if (document.querySelector('.u13-scroll-progress')) return;
    const bar = document.createElement('div');
    bar.className = 'u13-scroll-progress';
    bar.setAttribute('aria-hidden', 'true');
    document.body.appendChild(bar);
    const update = () => {
      const max = Math.max(1, document.documentElement.scrollHeight - window.innerHeight);
      const p = Math.min(1, Math.max(0, window.scrollY / max));
      bar.style.setProperty('--u13-scroll', p.toFixed(4));
    };
    update();
    window.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
  }

  function initReveals(root = document) {
    const targets = [...root.querySelectorAll('[data-u13-reveal]:not(.u13-motion-bound)')];
    if (!targets.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('is-visible', 'u13-motion-bound'));
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('is-visible');
        const cards = entry.target.querySelectorAll('[data-u13-card]');
        cards.forEach((card, i) => {
          card.style.transitionDelay = `${Math.min(i, 8) * 34}ms`;
          requestAnimationFrame(() => card.classList.add('u13-card-entered'));
        });
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -4% 0px' });

    targets.forEach((el) => {
      el.classList.add('u13-motion-bound');
      observer.observe(el);
    });
  }

  function initRails(root = document) {
    root.querySelectorAll('[data-u13-rail]').forEach((rail) => {
      if (rail.dataset.u13RailBound === 'true') return;
      rail.dataset.u13RailBound = 'true';
      const section = rail.closest('section');
      const prev = section?.querySelector('[data-u13-rail-prev]');
      const next = section?.querySelector('[data-u13-rail-next]');
      const amount = () => {
        const first = rail.querySelector(':scope > *');
        if (first) return Math.max(first.getBoundingClientRect().width + 10, 190);
        return Math.max(rail.clientWidth * .64, 220);
      };
      const update = () => {
        const max = Math.max(0, rail.scrollWidth - rail.clientWidth - 2);
        const atStart = rail.scrollLeft <= 2;
        const atEnd = rail.scrollLeft >= max;
        prev?.classList.toggle('is-disabled', atStart || max <= 2);
        next?.classList.toggle('is-disabled', atEnd || max <= 2);
        if (prev) prev.setAttribute('aria-disabled', String(atStart || max <= 2));
        if (next) next.setAttribute('aria-disabled', String(atEnd || max <= 2));
      };
      prev?.addEventListener('click', () => rail.scrollBy({ left: -amount(), behavior: 'smooth' }));
      next?.addEventListener('click', () => rail.scrollBy({ left: amount(), behavior: 'smooth' }));
      rail.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update, { passive: true });
      requestAnimationFrame(update);
    });
  }

  function initHeroMotion(root = document) {
    if (reduceMotion) return;
    root.querySelectorAll('[data-u13-hero-media]').forEach((media) => {
      if (media.dataset.u13HeroBound === 'true') return;
      media.dataset.u13HeroBound = 'true';
      const frame = media.closest('.u13-hero__frame');
      if (!frame) return;
      frame.addEventListener('pointermove', (event) => {
        if (window.innerWidth < 900) return;
        const rect = frame.getBoundingClientRect();
        const x = ((event.clientX - rect.left) / rect.width - .5) * 4;
        const y = ((event.clientY - rect.top) / rect.height - .5) * 3;
        media.style.translate = `${x}px ${y}px`;
      });
      frame.addEventListener('pointerleave', () => { media.style.translate = ''; });
    });
  }

  function initCinematicScroll(root = document) {
    if (reduceMotion) return;
    const items = [...root.querySelectorAll('[data-u13-cinematic]:not(.u13-cinematic-bound)')];
    if (!items.length) return;
    items.forEach((el) => el.classList.add('u13-cinematic-bound'));

    let ticking = false;
    const update = () => {
      ticking = false;
      items.forEach((el) => {
        const r = el.getBoundingClientRect();
        const travel = Math.max(1, window.innerHeight + r.height);
        const p = Math.min(1, Math.max(0, (window.innerHeight - r.top) / travel));
        el.style.setProperty('--u13-cinematic-progress', p.toFixed(3));
      });
    };
    const request = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(update);
    };
    update();
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request, { passive: true });
  }

  function initMagnetic(root = document) {
    if (reduceMotion || window.matchMedia('(hover: none)').matches) return;
    root.querySelectorAll('[data-u13-magnetic]:not(.u13-magnetic-bound)').forEach((el) => {
      el.classList.add('u13-magnetic-bound');
      el.addEventListener('pointermove', (e) => {
        const r = el.getBoundingClientRect();
        const x = (e.clientX - r.left - r.width / 2) * .09;
        const y = (e.clientY - r.top - r.height / 2) * .12;
        el.style.transform = `translate3d(${x}px,${y}px,0)`;
      });
      el.addEventListener('pointerleave', () => { el.style.transform = ''; });
    });
  }

  function initCardDepth(root = document) {
    if (reduceMotion || window.matchMedia('(hover: none)').matches) return;
    root.querySelectorAll('[data-u13-card]:not(.u13-depth-bound)').forEach((card) => {
      card.classList.add('u13-depth-bound');
      card.addEventListener('pointermove', (e) => {
        const r = card.getBoundingClientRect();
        const rx = ((e.clientY - r.top) / r.height - .5) * -1.2;
        const ry = ((e.clientX - r.left) / r.width - .5) * 1.2;
        card.style.transform = `perspective(900px) rotateX(${rx}deg) rotateY(${ry}deg)`;
      });
      card.addEventListener('pointerleave', () => { card.style.transform = ''; });
    });
  }

  function initSectionIdentity(root = document) {
    const scope = root === document ? document : root.ownerDocument || document;
    const sections = [...scope.querySelectorAll('#MainContent > .shopify-section:not(.u13-identity-bound)')];
    if (!sections.length) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      sections.forEach((section) => section.classList.add('u13-identity-section', 'u13-identity-bound'));
      return;
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('u13-identity-pulse');
        window.setTimeout(() => entry.target.classList.remove('u13-identity-pulse'), 1200);
        obs.unobserve(entry.target);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -8% 0px' });

    sections.forEach((section) => {
      section.classList.add('u13-identity-section', 'u13-identity-bound');
      observer.observe(section);
    });
  }

  function init(root = document) {
    initProgress();
    initReveals(root);
    initRails(root);
    initHeroMotion(root);
    initCinematicScroll(root);
    initMagnetic(root);
    initCardDepth(root);
    initSectionIdentity(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
